/**
 * PaymentSourceSelector.tsx
 *
 * Matches legacy payment_cc_type_select dropdown from payment-details-charge-gateway.php.
 * Shows saved cards (filtered by merchant) + Forward terminals + "New card"
 * + manual methods (Cash, Check, etc.).
 *
 * Token convention:
 *   - "new"                          → capture a fresh card via Forward.js
 *   - "terminal|{terminalForwardID}" → run the Forward Fiserv terminal flow
 *   - "manual|{paymentMethodID}"     → manual/cash payment (no gateway)
 *   - otherwise                      → a token for an existing saved card
 */
import { useQuery } from '@tanstack/react-query';
import {
  listBillingMethods,
  listPaymentMethodOptions,
  listPaymentTerminals,
  type BillingMethod,
  type PaymentTerminal,
} from '@/services/payments.service';
import { Badge, Spinner } from '@/components/ui';

interface PaymentSourceSelectorProps {
  patientId: number;
  value: string;
  merchantId?: number | null;
  onChange: (token: string, method: BillingMethod | null, terminal?: PaymentTerminal | null) => void;
  /** When true, the "New card" option is hidden from the dropdown. */
  hideNewOption?: boolean;
  /** When true, manual payment methods (Cash, Check, etc.) are shown. */
  showManualMethods?: boolean;
  /**
   * A card that is not among the patient's active cards but must stay selectable,
   * e.g. the removed card a recurring payment still charges. Without it the select
   * would display some other option than the one actually stored.
   */
  retainedCard?: { token: string; label: string; groupLabel: string } | null;
  /** Shown as a disabled first option while nothing is selected (value ''). */
  placeholder?: string;
}

/** Returns the terminal id when the selected token is a terminal handle, else null. */
export function parseTerminalToken(token: string | null | undefined): number | null {
  if (!token || !token.startsWith('terminal|')) return null;
  const id = Number(token.slice('terminal|'.length));
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** Returns the paymentMethodID when the selected token is a manual handle, else null. */
export function parseManualToken(token: string | null | undefined): number | null {
  if (!token || !token.startsWith('manual|')) return null;
  const id = Number(token.slice('manual|'.length));
  return Number.isFinite(id) && id > 0 ? id : null;
}

export function PaymentSourceSelector({
  patientId,
  value,
  merchantId,
  onChange,
  hideNewOption = false,
  showManualMethods = false,
  retainedCard = null,
  placeholder,
}: PaymentSourceSelectorProps) {
  const { data: methods, isLoading: methodsLoading } = useQuery({
    queryKey: ['patient-billing-methods', patientId],
    queryFn: () => listBillingMethods(patientId),
    staleTime: 60_000,
  });

  const { data: terminals, isLoading: terminalsLoading } = useQuery({
    queryKey: ['payment-terminals'],
    queryFn: () => listPaymentTerminals(),
    staleTime: 5 * 60_000,
  });

  const { data: manualOptions } = useQuery({
    queryKey: ['payment-method-options'],
    queryFn: listPaymentMethodOptions,
    enabled: showManualMethods,
    staleTime: 5 * 60_000,
  });

  const filteredMethods = (methods ?? []).filter((m) => m.endDate == null);

  const filteredTerminals = (terminals ?? []).filter((terminal) => {
    if (!merchantId) return true;
    return terminal.accountForwardId === merchantId;
  });

  const manualMethods = (manualOptions ?? []).filter((o) => o.active && !o.isCreditType);

  const showRetained = retainedCard != null
    && retainedCard.token !== ''
    && !filteredMethods.some((m) => m.token === retainedCard.token);

  if (methodsLoading || terminalsLoading) return <Spinner />;

  return (
    <label className="space-y-1 text-sm text-slate-700">
      <span className="font-medium">Payment Source</span>
      <select
        value={value}
        onChange={(e) => {
          const token = e.target.value;
          if (token.startsWith('terminal|')) {
            const terminalId = Number(token.slice('terminal|'.length));
            const terminal = filteredTerminals.find((t) => t.terminalForwardId === terminalId) ?? null;
            onChange(token, null, terminal);
            return;
          }
          if (token.startsWith('manual|')) {
            onChange(token, null, null);
            return;
          }
          const method = filteredMethods.find((m) => m.token === token) ?? null;
          onChange(token, method, null);
        }}
        className="w-full rounded-md border border-slate-300 px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
      >
        {placeholder && value === '' ? <option value="" disabled>{placeholder}</option> : null}
        {showRetained && retainedCard ? (
          <optgroup label={retainedCard.groupLabel}>
            <option value={retainedCard.token}>{retainedCard.label}</option>
          </optgroup>
        ) : null}
        {!hideNewOption && <option value="new">New card</option>}
        {filteredTerminals.length > 0 ? (
          <optgroup label="In-person terminals">
            {filteredTerminals.map((terminal) => (
              <option
                key={`terminal-${terminal.terminalForwardId}`}
                value={`terminal|${terminal.terminalForwardId}`}
              >
                Terminal: {terminal.title}
                {terminal.hsn ? ` (${terminal.hsn})` : ''}
              </option>
            ))}
          </optgroup>
        ) : null}
        {filteredMethods.length > 0 ? (
          <optgroup label="Saved cards">
            {filteredMethods.map((m) => (
              <option key={m.billingMethodID} value={m.token}>
                {m.title}
                {m.isDefault ? ' ★' : ''}
              </option>
            ))}
          </optgroup>
        ) : null}
        {showManualMethods && manualMethods.length > 0 ? (
          <optgroup label="Other methods">
            {manualMethods.map((opt) => (
              <option key={`manual-${opt.paymentMethodID}`} value={`manual|${opt.paymentMethodID}`}>
                {opt.name}
              </option>
            ))}
          </optgroup>
        ) : null}
      </select>
      {value !== 'new'
        && !value.startsWith('terminal|')
        && !value.startsWith('manual|')
        && filteredMethods.find((m) => m.token === value)?.isDefault && (
        <Badge variant="info" size="sm">Default</Badge>
      )}
      {value.startsWith('terminal|') && (
        <Badge variant="info" size="sm">In-person terminal</Badge>
      )}
      {value.startsWith('manual|') && (
        <Badge variant="neutral" size="sm">Manual payment</Badge>
      )}
    </label>
  );
}
